import React from "react";
import { ParentSize } from "@visx/responsive";
import NumberFlow from "@number-flow/react";
import MarketVolumeChart from "./MarketVolumeChart";

const MarketVolume = ({ site, period }) => {
  const [data, setData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('authToken'); // Assuming you store your JWT token here
        const response = await fetch(`/.netlify/functions/fetchP2PData?site=${site}&range=${period}`,  {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [site, period]);

  if (isLoading) {
    return <div className="w-full h-[50vh] flex items-center justify-center">Loading...</div>;
  }

  if (error) {
    return <div className="w-full h-[50vh] flex items-center justify-center text-red-500">Error: {error}</div>;
  }

  if (!data.length) {
    return <div className="w-full h-[50vh] flex items-center justify-center">No data available</div>;
  }

  // Calculate total volume based on selected period
  const calculateTotalVolume = () => {
    if (!data.length) return 0;

    // Sort data by date in descending order (most recent first)
    const sortedData = [...data].sort((a, b) => 
      new Date(b.trade_date).getTime() - new Date(a.trade_date).getTime()
    );

    switch (period) {
      case "24h":
        // For 24h, return the most recent data point's volume
        return parseFloat(sortedData[0].total_volume_dollar);
      case "7d":
        // Sum the 7 most recent data points
        return sortedData
          .slice(0, 7)
          .reduce((sum, item) => sum + parseFloat(item.total_volume_dollar), 0);
      case "28d":
        // Sum the 28 most recent data points
        return sortedData
          .slice(0, 28)
          .reduce((sum, item) => sum + parseFloat(item.total_volume_dollar), 0);
      default:
        return 0;
    }
  };

  const periodLabel = period === "24h" ? "24h" : period === "7d" ? "7d" : "28d";

  return (
    <div className="w-full h-[50vh] relative flex flex-col items-center">
      {/* USD Value Display - Positioned Above the Chart */}
      <div className="text-[#667EEA] text-2xl font-bold mb-2">
        <span className="text-gray-400 mr-2">{periodLabel}:</span>
        <NumberFlow 
          value={calculateTotalVolume()} 
          prefix="$" 
          className="text-green-500 text-2xl font-bold"
        />
      </div>

      {/* Chart */}
      <div className="w-full h-full">
        <ParentSize>
          {({ width, height }) => (
            <MarketVolumeChart 
              width={width} 
              height={height} 
              data={data}
            />
          )}
        </ParentSize>
      </div>
    </div>
  );
};

export default MarketVolume;
